/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import {
  Button, Grid, Form, Divider, Dropdown,
  Segment, Dimmer, Loader, Checkbox
} from "semantic-ui-react";
import ApexCharts from "apexcharts";
import EthernetIPMonitorReport from "./EthernetIPMonitorReport";
import Strings from "../utilities/StringSelector";
import Constants from "../utilities/Constants";
import Utils from "../utilities/Utils";

const UtilsInstance = new Utils();

const chartLocale = {
  name: "application-language",
  options: {
    months: Strings.CHART_MONTHS,
    shortMonths: Strings.CHART_SHORT_MONTHS,
    days: Strings.CHART_DAYS,
    shortDays: Strings.CHART_SHORT_DAYS,
    toolbar: {
      exportToSVG: Strings.CHART_DOWNLOAD_SVG,
      exportToPNG: Strings.CHART_DOWNLOAD_PNG,
      exportToCSV: Strings.CHART_DOWNLOAD_CSV,
      menu: Strings.CHART_DOWNLOAD,
      selection: Strings.CHART_SELECT,
      selectionZoom: Strings.CHART_SELECT_ZOOM,
      zoomIn: Strings.CHART_ZOOM_IN,
      zoomOut: Strings.CHART_ZOOM_OUT,
      pan: Strings.CHART_PAN,
      reset: Strings.CHART_RESET_ZOOM
    }
  }
};

const dropdownOptions = [
  {
    key: Constants.CONFORM,
    value: Constants.CONFORM,
    text: Strings.CONFORM_STRING
  },
  {
    key: Constants.CONFORM_W_RESTRICTION,
    value: Constants.CONFORM_W_RESTRICTION,
    text: Strings.CONFORM_W_RESTRICTION_STRING
  },
  {
    key: Constants.NON_CONFORM,
    value: Constants.NON_CONFORM,
    text: Strings.NON_CONFORM_STRING
  }
];

let pingGraphOptions = {
  series: [{ data: [] }],
  title: {
    text: Strings.PING_GRAPH,
    align: "center",
    style: {
      fontSize: "14px",
      fontFamily: "Helvetica, Arial, sans-serif"
    }
  },
  chart: {
    id: "ping-graph",
    type: "line",
    locales: [chartLocale],
    defaultLocale: "application-language",
    zoom: {
      type: "x",
      enabled: true,
      autoScaleYaxis: true
    },
    toolbar: {
      autoSelected: "reset"
    },
    animations: {
      enabled: false,
      easing: "linear",
      speed: 500,
      animateGradually: {
        enabled: true,
        delay: 150
      },
      dynamicAnimation: {
        enabled: true,
        speed: 350
      }
    },
  },
  colors: [Constants.TEAL_HEX, Constants.ORANGE_HEX, Constants.GREY_HEX, Constants.RED_HEX, Constants.BLUE_HEX],
  markers: {
    size: 0
  },
  dataLabels: {
    enabled: false
  },
  stroke: {
    curve: "smooth",
    width: 2
  },
  xaxis: {
    type: "numeric",
    title: {
      text: Strings.TIME
    },
    crosshairs: {
      show: "true"
    },
    tooltip: {
      enabled: false
    },
    labels: {
      show: true,
      formatter: function (value) {
        let date = new Date(value);

        let hour = date.getHours();
        let minutes = date.getMinutes();
        let seconds = date.getSeconds();

        if (hour < 10) {
          hour = "0" + hour.toString();
        }

        if (minutes < 10) {
          minutes = "0" + minutes.toString();
        }

        if (seconds < 10) {
          seconds = "0" + seconds.toString();
        }

        return hour + ":" + minutes + ":" + seconds;
      }
    }
  },
  yaxis: {
    min: 0,
    max: 2,
    labels: {
      formatter: function (val) {
        return val.toFixed(2) + Strings.MILLISECONDS_SUFFIX;
      },
    },
    title: {
      text: Strings.PING_MILLISECONDS
    },
  },
  annotations: {
    position: "front",
    xaxis: []
  }
};

class Report extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isGenerateReportDimmerActive: false,

      savedClients: [],
      savedExecutors: [],

      // User input data
      clientProjectName: "",
      clientNetworkName: "",
      clientLocation: "",
      clientCompanyName: "",
      clientAccountableName: "",
      clientAddress: "",
      clientCity: "",
      clientTel: "",
      executingCompanyName: "",
      executingAccountableName: "",
      executingTel: "",
      executingEmail: "",
      notes: "",
      conformity: "",
      isIncludePingGraphsChecked: false,
      // The report
      report: ""
    };
  }

  setGeneratingDimmerState = (isActive) => {
    if (!isActive) {
      this.setState({ isGenerateReportDimmerActive: isActive }, () => {
        alert(Strings.GENERATING_REPORT_SUCCESS);
      });
    }
    else {
      this.setState({ isGenerateReportDimmerActive: isActive });
    }
  }

  // Verifies if the user has the credentials to be logged in
  // If he does he is re-directed to the configuration page
  // If he doesn't he is re-directed to the login page
  isUserLoggedIn = () => {
    if (sessionStorage.auth !== Constants.AUTH_STR) {
      this.props.history.push({ pathname: "/login" });
    }
  }

  // ----------------- Data retrieval functions ----------------
  retrieveMonitoringData = async (userInput) => {
    this.setGeneratingDimmerState(true);

    let promissesArray = [];

    promissesArray.push( // [0]
      fetch(Constants.REQUEST_IP_ADDRESS + "networkAvailability", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.percentageAvailable)
    );

    promissesArray.push( // [1]
      fetch(Constants.REQUEST_IP_ADDRESS + "config", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
    );

    promissesArray.push( // [2]
      fetch(Constants.REQUEST_IP_ADDRESS + "startTime", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.startTime)
    );

    promissesArray.push( // [3]
      fetch(Constants.REQUEST_IP_ADDRESS, { mode: "cors" })
        .then(res => res.json())
        .then(data => UtilsInstance.filterEmptyTendencyAndDuplicatedDevices(data))
    );

    promissesArray.push( // [4]
      fetch(Constants.REQUEST_IP_ADDRESS, { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
    );

    promissesArray.push( // [5]
      fetch(Constants.REQUEST_IP_ADDRESS + "tcpIpInterface", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
    );

    promissesArray.push( // [6]
      fetch(Constants.REQUEST_IP_ADDRESS + "listIdentity", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
    );

    promissesArray.push( // [7]
      fetch(Constants.REQUEST_IP_ADDRESS + "ethernetLink", { mode: "cors" })
        .then(res => res.json())
        .then((data) => data)
    );

    promissesArray.push( // [8]
      fetch(Constants.REQUEST_IP_ADDRESS + "deviceLevelRing", { mode: "cors" })
        .then(res => res.json())
        .then((data) => data)
        .catch((error) => {
          console.log("Ocorreu um erro: ", error);
          return [];
        })
    );

    promissesArray.push( // [9]
      fetch(Constants.REQUEST_IP_ADDRESS + "mediaCounters", { mode: "cors" })
        .then(res => res.json())
        .then((data) => data)
    );

    promissesArray.push( // [10]
      fetch(Constants.REQUEST_IP_ADDRESS + "interfaceCounters", { mode: "cors" })
        .then(res => res.json())
        .then((data) => data)
    );

    promissesArray.push( // [11]
      fetch(Constants.REQUEST_IP_ADDRESS + "log", { mode: "cors" })
        .then(res => res.json())
        .then((data) => data)
    );

    promissesArray.push( // [12]
      fetch(Constants.REQUEST_IP_ADDRESS + "tag", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.tag)
    );

    promissesArray.push( // [13]
      fetch(Constants.REQUEST_IP_ADDRESS + "wifiConfig", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
    );

    promissesArray.push( // [14]
      fetch(Constants.REQUEST_IP_ADDRESS + "wifiStatus", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.status)
    );

    promissesArray.push( // [15]
      fetch(Constants.REQUEST_IP_ADDRESS + "deviceStatistics", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.devices)
    );

    promissesArray.push( // [16]
      fetch(Constants.REQUEST_IP_ADDRESS + "configEth1", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
    );

    promissesArray.push( // [17]
      fetch(Constants.REQUEST_IP_ADDRESS + "eth1Status", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
    );

    let resolvedPromisses = await Promise.all(promissesArray);

    let networkDevices = [];

    let networkAvailability = resolvedPromisses[0];
    let configData = resolvedPromisses[1];
    let startTime = resolvedPromisses[2];
    let nowMillis = Date.now();

    let pingGraphDevices = resolvedPromisses[3].sort((a, b) => {
      const num1 = Number(a.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      const num2 = Number(b.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      return num1 - num2;
    });

    // Live List
    let livelist = resolvedPromisses[4].sort((a, b) => {
      const num1 = Number(a.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      const num2 = Number(b.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      return num1 - num2;
    });

    // TCP IP Interface
    let tcpIpInterface = resolvedPromisses[5];

    for (let i = 0; i < livelist.length; i++) {
      if (livelist[i].protocol === Strings.ETHERNET_IP) {
        for (let j = 0; j < tcpIpInterface.length; j++) {
          if (livelist[i].mac === tcpIpInterface[j].id) {
            livelist[i].config_method = tcpIpInterface[j].config_method;
            break;
          }
        }
      }
      else {
        livelist[i].config_method = "";
      }
    }

    // Inventory
    let inventory = resolvedPromisses[6].sort((a, b) => {
      const num1 = Number(a.socket_sin_address.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      const num2 = Number(b.socket_sin_address.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      return num1 - num2;
    });

    // Ports
    let ports = resolvedPromisses[7].sort((a, b) => {
      const num1 = Number(a.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      const num2 = Number(b.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      return num1 - num2;
    });

    // Device Level Ring
    let deviceLevelRing = resolvedPromisses[8];

    for (let i = 0; i < tcpIpInterface.length; i++) {
      networkDevices.push({
        "id": tcpIpInterface[i].id,
        "ip": tcpIpInterface[i].ip,
        "acd_capable": tcpIpInterface[i].acd_capable,
        "acd_status": tcpIpInterface[i].acd_status
      });
    }

    if (deviceLevelRing !== undefined && deviceLevelRing !== null) {

      for (let i = 0; i < deviceLevelRing.length; i++) {
        for (let j = 0; j < networkDevices.length; j++) {
          if (deviceLevelRing[i].id === networkDevices[j].id) {
            networkDevices[j].network_status = deviceLevelRing[i].network_status;
            networkDevices[j].network_topology = deviceLevelRing[i].network_topology;
            networkDevices[j].announce_based_ring_node = deviceLevelRing[i].announce_based_ring_node;
            networkDevices[j].beacon_based_ring_node = deviceLevelRing[i].beacon_based_ring_node;
            networkDevices[j].flush_table_frame_capable = deviceLevelRing[i].flush_table_frame_capable;
            networkDevices[j].redundant_gateway_capable = deviceLevelRing[i].redundant_gateway_capable;
            networkDevices[j].supervisor_capable = deviceLevelRing[i].supervisor_capable;
            break;
          }
        }
      }
    }

    if (networkDevices.length > 0) {
      networkDevices = networkDevices.sort((a, b) => {
        const num1 = Number(a.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
        const num2 = Number(b.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
        return num1 - num2;
      });
    }

    // Media Counters
    let mediaCounters = resolvedPromisses[9].sort((a, b) => {
      const num1 = Number(a.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      const num2 = Number(b.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      return num1 - num2;
    });

    // Interface Counters
    let interfaceCounters = resolvedPromisses[10].sort((a, b) => {
      const num1 = Number(a.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      const num2 = Number(b.ip.split(".").map((num) => (`000${num}`).slice(-3)).join(""));
      return num1 - num2;
    });

    // Log
    let log = resolvedPromisses[11];
    let tag = resolvedPromisses[12];
    let wifiConfigData = resolvedPromisses[13];
    let wifiStatus = resolvedPromisses[14];

    // let topology = await fetch(Constants.REQUEST_IP_ADDRESS + "topology", { mode: "cors" })
    //   .then(res => res.json())
    //   .then(data => data);

    let imgURIs = [];

    if (this.state.isIncludePingGraphsChecked) {
      imgURIs = await this.getPingGraphImgURIs(pingGraphDevices, startTime);
    }

    // Wifi configuration
    if (wifiStatus !== Constants.CONNECTED_STATUS) {
      wifiConfigData = {
        "wifiMode": "",
        "hiddenSSID": false,
        "wifiName": "",
        "wifiAddr": "",
        "wifiNetmask": "",
        "wifiGateway": "",
        "wifiDNS": "",
        "wifiPwd": ""
      };
    }

    let officeEthStatus = resolvedPromisses[17].status;
    let officeEthConfigData = "";

    if (officeEthStatus === Constants.CONNECTED_STATUS) {
      officeEthConfigData = resolvedPromisses[16];
    }
    else {
      officeEthConfigData = {
        "eth1Addr": "",
        "eth1DNS": "",
        "eth1Gateway": "",
        "eth1Mode": "",
        "eth1Netmask": ""
      };
    }

    let numOfDevices = resolvedPromisses[15];

    this.setState({
      report:
        <EthernetIPMonitorReport
          networkAvailability={networkAvailability}
          tag={tag}
          configData={configData}
          wifiConfigData={wifiConfigData}
          officeEthConfigData={officeEthConfigData}
          startTime={startTime}
          nowMillis={nowMillis}
          userInput={userInput}
          livelist={livelist}
          inventory={inventory}
          ports={ports}
          conformity={this.state.conformity}
          //topology={topology}
          networkDevices={networkDevices}
          mediaCounters={mediaCounters}
          interfaceCounters={interfaceCounters}
          numOfDevices={numOfDevices}
          log={log}
          includesPingGraph={this.state.isIncludePingGraphsChecked}
          imgURIs={imgURIs}
        />
    });
  }

  getPingGraphImgURIs = async (liveListData, startTime) => {
    let imgURIs = [];
    document.getElementById("chart").style.display = "block";

    // Render the chart
    let chart = new ApexCharts(document.querySelector("#chart"), pingGraphOptions);
    chart.render();

    for (let i = 0; i < liveListData.length; i++) {

      let imgURIObj = { "ip": -1, "graph": "" };
      let options = pingGraphOptions;
      let currentSeriesMax = Math.ceil(Math.max(...liveListData[i].tendency));

      options = {
        ...options,
        yaxis: {
          ...options.yaxis,
          max: currentSeriesMax
        },
        series: [
          {
            name: liveListData[i].ip,
            data: this.buildGraphData(liveListData[i].tendency, liveListData[i].tendencyTime, startTime, liveListData[i].ntp)
          }
        ]
      };

      chart.updateOptions(options, false, false, false);

      imgURIObj.graph = await chart.dataURI();
      imgURIObj.ip = await liveListData[i].ip;

      // Save both waves URIs
      imgURIs.push(imgURIObj);
    }

    document.getElementById("chart").style.display = "none";

    chart.destroy();

    return imgURIs;
  }

  buildGraphData = (tendency, tendencyTime, startTime, ntp) => {
    let data = [];
    let pingTime = 0;

    for (let i = 0; i < tendency.length; i++) {

      if (ntp) {
        pingTime = Date.parse(tendencyTime[i].substring(0, tendencyTime[i].length - 4));
      }
      else {
        const eventTimeInMillis = tendencyTime[i] * 1000;
        const uptimeInMillis = startTime * 1000;
        const timeNow = new Date().getTime();
        pingTime = timeNow - uptimeInMillis + eventTimeInMillis;
      }

      data.push([pingTime, tendency[i]]);
    }

    return data;
  }

  // ----------------- Event Handlers -----------------
  handleChange = (e, { name, value }) => this.setState({ [name]: value })

  handleConformityDropdownItemClick = (event, info) => {
    this.setState({ conformity: info.value });
  }

  toggleIncludePingGraphsCheckbox = () => {
    this.setState({ isIncludePingGraphsChecked: !this.state.isIncludePingGraphsChecked });
  }

  handleSubmit = () => {
    let clientImg = "";
    let executorImg = "";

    if (document.getElementById("client-img").files[0] !== undefined) {
      clientImg = URL.createObjectURL(document.getElementById("client-img").files[0]);
    }

    if (document.getElementById("executor-img").files[0] !== undefined) {
      executorImg = URL.createObjectURL(document.getElementById("executor-img").files[0]);
    }

    this.setState({ report: "" }, () => {
      let userInput = {
        // Client info
        clientProjectName: this.state.clientProjectName,
        clientNetworkName: this.state.clientNetworkName,
        clientLocation: this.state.clientLocation,
        clientCompanyName: this.state.clientCompanyName,
        clientAccountableName: this.state.clientAccountableName,
        clientAddress: this.state.clientAddress,
        clientCity: this.state.clientCity,
        clientTel: this.state.clientTel,
        clientImg: clientImg,
        // Executing company info
        executingCompanyName: this.state.executingCompanyName,
        executingAccountableName: this.state.executingAccountableName,
        executingTel: this.state.executingTel,
        executingEmail: this.state.executingEmail,
        executorImg: executorImg,
        // Notes
        notes: this.state.notes,
        // Conformity
        conformity: this.state.conformity
      };

      this.retrieveMonitoringData(userInput);
    });
  }

  // ----------------- Lifecycle Methods ----------------
  componentDidMount() {
    this.isUserLoggedIn();
  }

  render() {
    const {
      clientProjectName,
      clientNetworkName,
      clientLocation,
      clientCompanyName,
      clientAccountableName,
      clientAddress,
      clientCity,
      clientTel,
      executingCompanyName,
      executingAccountableName,
      executingTel,
      executingEmail,
      notes
    } = this.state;

    return (
      <div style={divStyle}>

        <Grid columns={2} doubling stretched>
          <Grid.Column>
            <Segment>

              <Divider horizontal>{Strings.CLIENT_INFO}</Divider>
              <Form>
                <Form.Input label={Strings.PROJECT_NAME_LABEL} name='clientProjectName' value={clientProjectName} onChange={this.handleChange} />
                <Form.Input label={Strings.NETWORK_NAME_LABEL} name='clientNetworkName' value={clientNetworkName} onChange={this.handleChange} />
                <Form.Input label={Strings.LOCATION_LABEL} name='clientLocation' value={clientLocation} onChange={this.handleChange} />
                <Form.Input label={Strings.COMPANY_NAME_LABEL} name='clientCompanyName' value={clientCompanyName} onChange={this.handleChange} />
                <Form.Input label={Strings.ACCOUNTABLE_NAME_LABEL} name='clientAccountableName' value={clientAccountableName} onChange={this.handleChange} />
                <Form.Input label={Strings.ADDRESS_LABEL} name='clientAddress' value={clientAddress} onChange={this.handleChange} />
                <Form.Input label={Strings.CITY_LABEL} name='clientCity' value={clientCity} onChange={this.handleChange} />
                <Form.Input label={Strings.PHONE_LABEL} name='clientTel' value={clientTel} onChange={this.handleChange} />
                <Form.Input id="client-img" label={Strings.CLIENT_LOGO_LABEL} type="file" accept=".png,.jpg,.jpeg" />
              </Form>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment>

              <Divider horizontal>{Strings.EXECUTING_COMPANY_INFO}</Divider>

              <Form>
                <Form.Input label={Strings.COMPANY_NAME_LABEL} name='executingCompanyName' value={executingCompanyName} onChange={this.handleChange} />
                <Form.Input label={Strings.ACCOUNTABLE_NAME_LABEL} name='executingAccountableName' value={executingAccountableName} onChange={this.handleChange} />
                <Form.Input label={Strings.PHONE_LABEL} name='executingTel' value={executingTel} onChange={this.handleChange} />
                <Form.Input label={Strings.EMAIL_LABEL} name='executingEmail' value={executingEmail} onChange={this.handleChange} />
                <Form.Input id="executor-img" label={Strings.EXECUTING_COMPANY_LOGO_LABEL} type="file" accept=".png,.jpg,.jpeg" />
              </Form>
            </Segment>

            <Grid columns={2} doubling>
              <Grid.Column verticalAlign="middle">

                <Segment>
                  <Divider horizontal>{Strings.CONFORMITY}</Divider>
                  <Dropdown
                    fluid
                    selection
                    placeholder={Strings.DROPDOWN_PLACEHOLDER}
                    options={dropdownOptions}
                    onChange={this.handleConformityDropdownItemClick} />
                </Segment>
              </Grid.Column>

              <Grid.Column verticalAlign="middle">
                <Segment>
                  <Divider horizontal>{Strings.PING_GRAPHS}</Divider>
                  <Checkbox
                    label={Strings.INCLUDE_IN_REPORT}
                    onChange={this.toggleIncludePingGraphsCheckbox}
                    checked={this.state.isIncludePingGraphsChecked} />
                </Segment>

              </Grid.Column>

            </Grid>

            <Segment>
              <Divider horizontal>{Strings.ANNOTATIONS}</Divider>
              <Form>
                <Form.Input type="text" name='notes' value={notes} onChange={this.handleChange} />
              </Form>
            </Segment>
          </Grid.Column>
        </Grid>

        <Button
          style={buttonStyle}
          color="teal"
          onClick={this.handleSubmit}>
          {Strings.GENERATE_REPORT}
        </Button>

        <PDFDownloadLink
          style={pdfLinkStyle}
          document={this.state.report}
          fileName={Strings.REPORT_PDF_FILENAME}>
          {({ loading }) => {
            if (loading) {
              return Strings.FINISHING;
            }
            else {
              if (this.state.isGenerateReportDimmerActive) {
                this.setGeneratingDimmerState(false);
              }
              return Strings.SAVE_REPORT;
            }
          }}
        </PDFDownloadLink>

        <div id="chart" style={chartStyle}></div>
        <div id="barchart" style={chartStyle}></div>

        <Dimmer page active={this.state.isGenerateReportDimmerActive} >
          <Loader size="big">{Strings.GENERATING_REPORT_WAITING}</Loader>
        </Dimmer>

      </div>
    );
  }
}

const chartStyle = {
  width: "1026px",
  height: "580px",
  display: "none",
  visibility: "hidden"
};

const divStyle = {
  margin: "2vh 2vw",
  textAlign: "center"
};

const buttonStyle = {
  margin: "2vh auto"
};

const pdfLinkStyle = {
  marginLeft: "2vw"
};

export default withRouter(Report);
